import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { IoCloseOutline } from "react-icons/io5"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"

import { queryOpenAPIForDescription } from "@api/firebaseWrappedApis"

export interface DescriptionBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

const DescriptionBottomSheetV3 = forwardRef<
  DescriptionBottomSheetHandle,
  {
    register: any
    errors: any
    onChange: (value: string) => void
    description: string
    isDefaultBg?: boolean
    eventName?: string
    date?: string
    location?: string
  }
>(
  (
    {
      register,
      errors,
      onChange,
      description,
      isDefaultBg = false,
      eventName = "",
      date = "",
      location = "",
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Get the registration details for the description field
    const { ref: registerRef, ...rest } = register("description")

    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsVisible(true)
      },
      closeModal: () => {
        setIsDescriptionGenerated(false)
        setIsGeneratingDescription(false)
        setIsVisible(false)
      },
    }))

    useEffect(() => {
      if (isVisible && textareaRef.current) {
        textareaRef.current.focus()
      }
    }, [isVisible])

    const [isGeneratingDescription, setIsGeneratingDescription] =
      useState(false)
    const [isDescriptionGenerated, setIsDescriptionGenerated] = useState(false)
    const generateEventDescriptionData = async () => {
      const response = await queryOpenAPIForDescription({
        eventName,
        date,
        location,
      })
      if (response.success) {
        // Keep the newline characters as is for textarea
        setIsDescriptionGenerated(true)
        onChange(response.data)
        setIsGeneratingDescription(false)
      } else {
        setIsGeneratingDescription(false)
        showErrorToast("Something went wrong")
      }
    }

    if (!isVisible) return null

    return (
      <div
        className="fixed inset-0 flex justify-center bg-black bg-opacity-50 z-[120] items-end md:items-center place-items-center "
        onClick={() => {
          setIsDescriptionGenerated(false)
          setIsGeneratingDescription(false)
          setIsVisible(false)
        }}
      >
        <BlurWrapper
          overlayOpacityDefault="20"
          className={`w-full md:max-w-sm ${
            isDefaultBg ? "!bg-black/50" : " !bg-gray-500/10"
          } rounded-t-2xl md:rounded-2xl p-6 relative flex flex-col cursor-auto  h-[45vh] max-h-[45vh]  gap-4`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex items-center justify-center">
            <div className="text-white text-lg font-semibold text-center w-full">
              Description
            </div>
            <div
              className="absolute right-0"
              onClick={() => {
                setIsVisible(false)
                setIsDescriptionGenerated(false)
                setIsGeneratingDescription(false)
              }}
            >
              <IoCloseOutline color="#fff" size={20} />
            </div>
          </div>
          <textarea
            {...rest}
            ref={(e) => {
              registerRef(e)
              textareaRef.current = e
            }}
            onChange={(e) => onChange(e.target.value)}
            className="w-full flex-1 resize-none  bg-transparent text-white min-h-[150px] text-base focus:outline-none"
            placeholder={
              isGeneratingDescription
                ? "✨ Generating..."
                : "Write your description here"
            }
            value={description}
          />
          <div className="flex items-center justify-center">
            {isDescriptionGenerated ? (
              <div className="text-white text-sm font-medium text-center w-full ">
                <div className="flex  items-center  gap-4 bg-white/10 justify-between py-3 rounded-xl">
                  <div
                    className="text-white text-sm font-medium text-center w-full cursor-pointer flex-1"
                    onClick={() => {
                      setIsDescriptionGenerated(false)
                    }}
                  >
                    ✅ &nbsp; Save
                  </div>
                  <div
                    className="text-white text-sm font-medium text-center w-full cursor-pointer flex-1"
                    onClick={() => {
                      setIsDescriptionGenerated(false)
                      onChange("")
                      setIsGeneratingDescription(true)
                      generateEventDescriptionData()
                    }}
                  >
                    🔁 &nbsp; Try again
                  </div>
                </div>
              </div>
            ) : !isGeneratingDescription ? (
              <div
                className="text-white text-sm font-medium text-center bg-white/10 rounded-md py-3 px-4 w-fit cursor-pointer"
                onClick={() => {
                  if (!eventName) {
                    showErrorToast("Please enter the event name")
                    return
                  }
                  onChange("")
                  setIsGeneratingDescription(true)
                  generateEventDescriptionData()
                }}
              >
                ✨ Ask AI
              </div>
            ) : null}
          </div>
        </BlurWrapper>
      </div>
    )
  },
)

export default DescriptionBottomSheetV3
